import { useMemo } from 'react';
import type { FC } from 'react';
import { useTranslation } from '@/i18n';

import Table from '@/components/ui/tables/Table';
import { PrimaryButton } from '@/components/ui/buttons';
import { IconScout } from '@/components/ui/images/IconScout';
import { ExportDropdown } from '@/components/ui/filters/ExportDropdown';
import { FilterDropdown } from '@/components/ui/filters/FilterDropdown';
import { Selector } from '@/components/ui/inputs/selector';
import { PermissionGuard } from '@/components/layout';
import { useLocationList } from '@/hooks/checkInLocation';


function booleanToFilterValue(value: boolean | null | undefined): string {
  if (value === true) return 'true';
  if (value === false) return 'false';
  return '';
}

function filterValueToBoolean(value: string | string[]): boolean | null {
  const stringValue = Array.isArray(value) ? (value[0] ?? '') : value;
  if (stringValue === 'true') return true;
  if (stringValue === 'false') return false;
  return null;
}

export const LocationListTab: FC = () => {
  const { t: translate } = useTranslation('checkInLocation');

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    actions,
    onSearch,
    onSortChange,
    isLoading,
    searchPlaceholder,
    openCreatePanel,
    onDownload,
    isExporting,
    filters,
    onFilterChange,
    onSelectionChange,
    bulkActions,
    tableSelectionKey,
    isSelectAllTotalActive,
    onSelectAllTotal,
    onClearSelectionTotal,
    canDelete,
  } = useLocationList();

  const geolocationOptions = useMemo(() => [
    { value: '', label: translate('filters.all') },
    { value: 'true', label: translate('list.geolocation.yes') },
    { value: 'false', label: translate('list.geolocation.no') },
  ], [translate]);

  const teleworkOptions = useMemo(() => [
    { value: '', label: translate('filters.all') },
    { value: 'true', label: translate('list.telework.yes') },
    { value: 'false', label: translate('list.telework.no') },
  ], [translate]);

  return (
    <div data-testid="tab-locations-content">
      <div className="mb-4">
        <PermissionGuard action="create">
          <PrimaryButton
            iconLeft={<IconScout name="plus" size="sm" />}
            onClick={openCreatePanel}
            data-testid="new-location-button"
          >
            {translate('buttons.newLocation')}
          </PrimaryButton>
        </PermissionGuard>
      </div>

      <div className="flex flex-wrap gap-3 items-center justify-between mb-4">
        <FilterDropdown
          extraFilters={(
            <div className="flex flex-col gap-3 pt-2">
              <Selector
                label={translate('filters.geolocation.label')}
                options={geolocationOptions}
                value={booleanToFilterValue(filters.hasGeolocation)}
                onChange={(value) => {
                  onFilterChange({ ...filters, hasGeolocation: filterValueToBoolean(value) });
                }}
                data-testid="filter-geolocation-selector"
              />
              <Selector
                label={translate('filters.telework.label')}
                options={teleworkOptions}
                value={booleanToFilterValue(filters.isRemoteWork)}
                onChange={(value) => {
                  onFilterChange({ ...filters, isRemoteWork: filterValueToBoolean(value) });
                }}
                data-testid="filter-telework-selector"
              />
            </div>
          )}
        />
        <ExportDropdown
          onExport={onDownload}
          placement="right"
          isLoading={isExporting}
          availableFormats={['EXCEL']}
        />
      </div>

      <Table
        key={tableSelectionKey}
        columns={columns}
        data={rows}
        totalRows={total}
        isLoading={isLoading}
        searchable
        searchPlaceholder={searchPlaceholder}
        onSearch={onSearch}
        serverSideSort
        onSortChange={onSortChange}
        actions={actions}
        selectable={canDelete}
        onSelectionChange={onSelectionChange}
        bulkActions={bulkActions}
        isSelectAllTotalActive={isSelectAllTotalActive}
        onSelectAllTotal={onSelectAllTotal}
        onClearSelectionTotal={onClearSelectionTotal}
        pagination={{
          page,
          pageSize,
          total,
          onChange: setPage,
          onPageSizeChange: setPageSize,
        }}
        data-testid="check-in-location-table"
      />
    </div>
  );
};

export default LocationListTab;
